import { CircularProgress, IconButton, Slider } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import type { WaybackItem, WaybackMetadata } from '@vannizhang/wayback-core';
import { debounce } from 'lodash';
import type { StyleSpecification } from 'mapbox-gl';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { getMapStyle } from '~/common/mapStyle';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { defaultBasemapStyle } from '~/common/types';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import { useIsMounted } from '~/hooks/useIsMounted';
import type { ProjectDetailsParams } from '~/pages/projectDetails';
import rootStore from '~/store';
import splitViewStore from '../SplitView/store';
import { getWaybackConfig } from './config';
import store from './store';

type TMapCoords = { lng: number; lat: number; z: number } | undefined;

interface Props {
  visible?: boolean;
  mapIndex: number;
}

export default function HistoricalImagery({ mapIndex, visible }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [waybackItems, setWaybackItems] = useState<WaybackItem[]>([]);
  const [currentItem, setCurrentItem] = useState<WaybackItem | null>(null);
  const [metadata, setMetadata] = useState<WaybackMetadata[]>([]);
  const [nearestItemIndex, setNearestItemIndex] = useState<number>(0);
  const maps = MapGL.useMap();
  const map = maps.current;
  const params = useParams({ strict: false }) as ProjectDetailsParams;
  const isMounted = useIsMounted();

  const loadItems = async () => {
    try {
      setIsLoading(true);
      setIsError(false);
      let p: TMapCoords;

      if (map) {
        const coords = map.getCenter();
        const z = map.getZoom();
        p = { lng: coords.lng, lat: coords.lat, z };
      }

      const res = await getWaybackConfig(p);

      if (isMounted()) {
        setWaybackItems([...res.waybackData]);
        if (res.waybackMetadata) {
          setMetadata([...res.waybackMetadata]);
        }
      }
    } catch {
      setIsError(true);
    } finally {
      if (isMounted()) {
        setIsLoading(false);
      }
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (!currentItem) {
      const val = waybackItems[waybackItems.length - 1] || null;
      setCurrentItem(val);

      if (val) {
        setNearestItemIndex(waybackItems.length - 1);
      } else {
        setNearestItemIndex(0);
      }
    } else {
      const current = currentItem;
      let minDiff: number = Infinity;
      let newItem: WaybackItem = currentItem;
      let nearestIndex: number = waybackItems.length - 1 || 0;

      waybackItems.forEach((item, i) => {
        const diff = Math.abs(item.releaseDatetime - current.releaseDatetime);

        if (diff < minDiff) {
          minDiff = diff;
          newItem = item;
          nearestIndex = i;
        }
      });

      setCurrentItem(newItem);
      setNearestItemIndex(nearestIndex);
    }
  }, [waybackItems]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (map) {
      loadItems();
    }

    const mapIds = Object.keys(maps);
    const debouncedListener = debounce(loadItems, 100);

    mapIds.forEach((id) => {
      const m = maps[id];
      if (m) {
        m.on('moveend', debouncedListener);
      }
    });

    return () => {
      mapIds.forEach((id) => {
        const m = maps[id];
        if (m) {
          m.off('moveend', debouncedListener);
        }
      });

      map?.getMap().setStyle(rootStore.preferredBasemap || defaultBasemapStyle);
    };
  }, [map]);

  const onChange = (i: number) => {
    if (!map) return;

    const item = waybackItems[i];

    // just in case a change is made and the value does not
    // exist in the local array anymore.
    if (!item) {
      setCurrentItem(null);
      return;
    }

    setCurrentItem(item);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (currentItem && map) {
      const waybackUrl = currentItem.itemURL;
      const tileUrl = waybackUrl.replace('level', 'z').replace('row', 'y').replace('col', 'x');

      // using a Set will remove duplicates
      const attributionList: Set<string> = new Set();

      metadata.forEach((p) => {
        if (p?.provider) {
          attributionList.add(p.provider);
        }
      });

      let attributions = `Source: ESRI ArcGIS, Wayback`;

      attributionList.forEach((a) => {
        attributions += `, ${a}`;
      });

      const style = getMapStyle({
        id: 'esriHistorical',
        name: currentItem.itemTitle,
        attribution: attributions,
        tileUrl,
      });

      if (isMounted()) {
        map.getMap().setStyle(style as StyleSpecification);
      }

      capturePosthogEvent(POSTHOG_EVENTS.mapHistoricalImageryViewed, {
        item_id: currentItem.itemID,
        item_title: currentItem.itemTitle,
        item_url: currentItem.itemURL,
        item_url_cg: tileUrl,
        ...(currentItem.layerIdentifier && {
          layer_identifier: currentItem.layerIdentifier,
        }),
        metadata_layer_item_id: currentItem.metadataLayerItemID,
        metadata_layer_url: currentItem.metadataLayerUrl,
        release_date_label: currentItem.releaseDateLabel,
        release_datetime: currentItem.releaseDatetime,
        release_num: currentItem.releaseNum,
        ...(params.projectId && { project_id: params.projectId }),
        ...(params.mapId && { map_id: params.mapId }),
        ...(params.tabId && { tab_id: params.tabId }),
      });
    }
  }, [currentItem]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const source = useMemo(() => {
    if (!currentItem) return null;

    return (
      <div className="flex w-full items-center space-x-2">
        <div className="font-bold">Image Date: </div>
        <div>{currentItem.releaseDateLabel}</div>
      </div>
    );
  }, [currentItem?.itemID]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const minmax = useMemo(() => {
    const min = waybackItems[0];
    const max = waybackItems[waybackItems.length - 1];

    let pagination: JSX.Element | null = null;

    if (waybackItems && waybackItems.length > 0 && currentItem) {
      const i = waybackItems.indexOf(currentItem);
      pagination = (
        <div>
          {i + 1} / {waybackItems.length}
        </div>
      );
    }

    if (min && max) {
      const minYear = min.releaseDateLabel?.split('-')[0] || null;
      const maxYear = max.releaseDateLabel?.split('-')[0] || null;

      return (
        <div className="flex w-full items-center justify-between px-[2.5rem] pb-3 text-xs">
          <div className="-translate-x-1/4 -rotate-90">{minYear}</div>
          {pagination}
          <div className="translate-x-1/4 -rotate-90">{maxYear}</div>
        </div>
      );
    }

    return null;
  }, [waybackItems, waybackItems.length, currentItem?.itemID]);

  const increment = (value: number) => {
    if (currentItem) {
      const currentIndex = waybackItems.indexOf(currentItem);

      if (currentIndex === 0 && value < 0) {
        return;
      } else if (currentIndex === waybackItems.length - 1 && value > 0) {
        return;
      }

      setCurrentItem(waybackItems[currentIndex + value]);
      setNearestItemIndex(currentIndex + value);
    }
  };

  if (!visible) {
    return null;
  }

  if (isLoading) {
    return (
      <Container mapIndex={mapIndex} className="items-center">
        <CircularProgress indeterminate />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container mapIndex={mapIndex} className="flex justify-center">
        <div>There was an error loading historical imagery.</div>
        <div>Please contact support.</div>
      </Container>
    );
  }

  return (
    <Container mapIndex={mapIndex} className="space-y-4">
      {source}
      <div className="flex items-center justify-between space-x-2">
        <IconButton
          variant="secondary"
          icon="chevronLeft"
          onClick={() => increment(-1)}
          aria-label="Previous Image Date"
        />
        <Slider
          label="Historical Image Date"
          min={0}
          max={waybackItems.length - 1}
          step={1}
          onValueChange={setNearestItemIndex}
          onValueCommit={([i]) => onChange(i)}
          value={nearestItemIndex}
          formatValue={(value: number) => waybackItems[value]?.releaseDateLabel ?? ''}
          labelClassName="hidden"
          valueClassName="hidden"
          className="gap-0"
          showTickMarks={true}
        />
        <IconButton variant="secondary" icon="chevronRight" onClick={() => increment(1)} aria-label="Next Image Date" />
      </div>
      {minmax}
    </Container>
  );
}

interface ContainerProps {
  mapIndex: number;
  children?: React.ReactNode;
  className?: string;
}

const Container = ({ mapIndex, className, children }: ContainerProps) => {
  const { splitView } = useSnapshot(splitViewStore);
  // Map index 1 only renders as the right-hand map in split view.
  const mapSide = mapIndex === 1 ? ' (right map)' : splitView ? ' (left map)' : '';

  return (
    <div
      className={`relative flex w-80 flex-col rounded-sm border border-solid !border-secondary/30 bg-white p-1.5 !text-primary ${className}`}
    >
      <div className="flex w-full items-center justify-between">
        <div className="text-2xs uppercase">historical imagery</div>
        <CloseIconButton onClick={() => store.reset(mapIndex)} aria-label={`Close historical imagery${mapSide}`} />
      </div>
      {children}
    </div>
  );
};
